import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Target,
  Activity,
  TrendingDown,
  ArrowUpRight,
  TrendingUp,
  Sparkles,
  Trophy,
} from "lucide-react";
import { type DateRangeOption } from "@/components/clicks/DateRangeFilter";
import { CopyableValue } from "@/components/clicks/CopyableValue";
import { useSpyserpInsights } from "@/hooks/useSpyserpInsights";

export default function InsightsTab() {
  const [searchParams] = useSearchParams();
  const dateRange = (searchParams.get("dateRange") ||
    "last_30_days") as DateRangeOption;
  const customFrom = searchParams.get("from") || undefined;
  const customTo = searchParams.get("to") || undefined;

  const dateRangeParams = useMemo(
    () => ({ dateRange, customFrom, customTo }),
    [dateRange, customFrom, customTo],
  );

  const { data, isPending } = useSpyserpInsights(dateRangeParams);

  if (isPending) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        Loading insights...
      </div>
    );
  }

  if (!data || !data.overview || data.overview.total_kw === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground border-2 border-dashed rounded-lg">
        <Activity className="w-10 h-10 mb-2 opacity-50" />
        <p className="font-medium">No data available for the selected period</p>
        <p className="text-sm">Try selecting a different date range.</p>
      </div>
    );
  }

  const { overview, winners, losers } = data;

  const totalMovement = overview.growing + overview.declining;
  const growingPercent = totalMovement
    ? Math.round((overview.growing / totalMovement) * 100)
    : 0;
  const decliningPercent = totalMovement
    ? Math.round((overview.declining / totalMovement) * 100)
    : 0;
  const top10Percent = overview.total_kw
    ? ((overview.top_10 / overview.total_kw) * 100).toFixed(1)
    : "0";

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Performance Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-5 flex flex-col gap-1">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium">Total Keywords</span>
                <Target className="w-4 h-4" />
              </div>
              <span className="text-2xl font-bold tabular-nums">{overview.total_kw}</span>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 flex flex-col gap-1">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium">Top 10 Keywords</span>
                <ArrowUpRight className="w-4 h-4" />
              </div>
              <span className="text-2xl font-bold tabular-nums">{overview.top_10}</span>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 flex flex-col gap-1">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium">Dropped from Top 50</span>
                <TrendingDown className="w-4 h-4" />
              </div>
              <span className="text-2xl font-bold tabular-nums">{overview.dropped_50}</span>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 flex flex-col gap-1">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium">
                  % keywords in Top 10
                </span>
                <Activity className="w-4 h-4" />
              </div>
              <span className="text-2xl font-bold tabular-nums">{top10Percent}%</span>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Keyword Movement</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="col-span-2 grid grid-cols-2 gap-4">
            <Card className="signal-card-positive border">
              <CardContent className="p-5">
                <div className="mb-4 flex items-center justify-between signal-text-positive">
                  <span className="text-sm font-semibold">Growing</span>
                  <TrendingUp className="h-4 w-4" />
                </div>
                <div className="mb-2 flex items-end justify-between">
                  <span className="text-3xl font-bold tabular-nums signal-text-positive">
                    {overview.growing}
                  </span>
                  <span className="text-xs font-medium tabular-nums signal-text-positive">
                    {growingPercent}%
                  </span>
                </div>
                <Progress value={growingPercent} className="h-2 bg-muted" />
              </CardContent>
            </Card>

            <Card className="signal-card-negative border">
              <CardContent className="p-5">
                <div className="mb-4 flex items-center justify-between signal-text-negative">
                  <span className="text-sm font-semibold">Declining</span>
                  <TrendingDown className="h-4 w-4" />
                </div>
                <div className="mb-2 flex items-end justify-between">
                  <span className="text-3xl font-bold tabular-nums signal-text-negative">
                    {overview.declining}
                  </span>
                  <span className="text-xs font-medium tabular-nums signal-text-negative">
                    {decliningPercent}%
                  </span>
                </div>
                <Progress
                  value={decliningPercent}
                  className="h-2 bg-muted [&_[data-slot=progress-indicator]]:bg-destructive"
                />
              </CardContent>
            </Card>
          </div>

          <Card className="badge-chart-5 border border-[var(--chart-5-subtle)] bg-[var(--chart-5-subtle)]">
            <CardContent className="p-5">
              <div className="mb-4 flex items-center justify-between text-[var(--chart-5-fg)]">
                <span className="text-sm font-semibold">New Keywords</span>
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-3xl font-bold tabular-nums text-[var(--chart-5-fg)]">
                  {overview.new_kw}
                </span>
                <span className="text-xs font-medium text-muted-foreground">
                  Fresh entries in rankings
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
        <Card className="border-border pt-0">
          <CardHeader className="signal-card-positive border-b pt-3 pb-3">
            <CardTitle className="flex items-center gap-2 text-sm signal-text-positive">
              <Trophy className="h-4 w-4" /> Top Winners
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Best performing domains
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {winners.length === 0 ? (
                <div className="p-4 text-sm text-center text-muted-foreground">
                  No data available
                </div>
              ) : (
                winners.map((d, i) => (
                  <div
                    key={d.domain}
                    className="flex items-center justify-between p-4 hover:bg-muted/30"
                  >
                    <div className="flex items-center gap-3">
                      <Badge className="badge-signal-positive shadow-none hover:opacity-90">
                        #{i + 1}
                      </Badge>
                      <div>
                        <CopyableValue value={d.domain} noTruncate className="font-semibold text-sm" />
                        <p className="text-xs text-muted-foreground">
                          {d.kw_count} keywords
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-medium tabular-nums signal-text-positive">
                      +{d.total_change} pos
                    </span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-border pt-0">
          <CardHeader className="signal-card-negative border-b pt-3 pb-3">
            <CardTitle className="flex items-center gap-2 text-sm signal-text-negative">
              <TrendingDown className="h-4 w-4" /> Top Losers
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Domains needing attention
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {losers.length === 0 ? (
                <div className="p-4 text-sm text-center text-muted-foreground">
                  No data available
                </div>
              ) : (
                losers.map((d, i) => (
                  <div
                    key={d.domain}
                    className="flex items-center justify-between p-4 hover:bg-muted/30"
                  >
                    <div className="flex items-center gap-3">
                      <Badge className="badge-signal-negative shadow-none hover:opacity-90">
                        #{i + 1}
                      </Badge>
                      <div>
                        <CopyableValue value={d.domain} noTruncate className="font-semibold text-sm" />
                        <p className="text-xs text-muted-foreground">
                          {d.kw_count} keywords
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-medium tabular-nums signal-text-negative">
                      {d.total_change} pos
                    </span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
